<template>
  <div
    class="gas-curve-chart"
    :class="{ 'is-static': isStatic, 'is-horizontal': isHorizontal }"
  >
    <div class="scatter">
      <ScatterChart
        v-bind="$attrs"
        draw-curve
        :draw-points="false"
        :data-sets="allDataSets"
        :tick-frequency-x="ddvTickFrequencies?.x"
        :tick-frequency-y="ddvTickFrequencies?.y"
      >
        <template #background="{ xScaler, margins, domState, yScaler }">
          <rect
            v-if="domState.plotWidth > 0 && domState.plotHeight > 0"
            class="plotBg"
            :x="margins.left"
            :y="margins.top"
            :width="domState?.plotWidth"
            :height="domState?.plotHeight"
          />
          <template v-if="allDataSets.every(ds => ds.points.length < 4)">
            <text
              x="50%"
              y="50%"
              dominant-baseline="middle"
              text-anchor="middle"
              fill="var(--plotColor0)"
              >No Data</text
            >
          </template>
          <template
            v-else-if="domState.plotWidth > 0 && domState.plotHeight > 0"
          >
            <Axis
              :label="
                allDataSets?.length > 0 ? allDataSets[0]?.yAxisLabel : undefined
              "
              :x="margins.left"
              :y="margins.top"
              :scaler="yScaler"
              :domain="aggregateY"
              :plot-margin-px="6"
              :tick-frequency="ddvTickFrequencies?.x"
              is-vertical
              draw-grid-lines
            />
            <Axis
              :label="
                allDataSets?.length > 0 ? allDataSets[0]?.xAxisLabel : undefined
              "
              :x="margins.left"
              :y="margins.top"
              :scaler="xScaler"
              :domain="aggregateX"
              :plot-margin-px="6"
              :tick-frequency="ddvTickFrequencies?.y"
              draw-grid-lines
            />

            <g
              v-for="threshold in pumpThresholds"
              :key="threshold.label"
              :transform="`translate(${margins.left} ${margins.top})`"
            >
              <ThresholdArea
                v-if="
                  typeof threshold.value !== 'number' &&
                  showPumpThresholds &&
                  isNumberDomain(threshold.value)
                "
                is-x
                :label="threshold.label"
                :scaler="xScaler"
                :domain="aggregateX"
                :color="threshold.color"
                :min="threshold.value.min"
                :max="threshold.value.max"
              />
            </g>
          </template>
        </template>

        <template #perSeries="{ dataSeriesNumber, nearestPoint, screenPoints }">
          <LinePlot
            :screen-points="screenPoints"
            :data-series-number="dataSeriesNumber"
            :curve-interpolator="
              dataSeriesNumber === 1 ? 'stair-step' : 'catmull-rom'
            "
            :stair-step-top-ratio="1"
            :is-active="activeDataSets[dataSeriesNumber]"
          />

          <NearestPoint
            v-if="nearestPoint?.node"
            :data-series-number="dataSeriesNumber"
            :nearest-point="nearestPoint"
          />
        </template>

        <template
          v-if="allDataSets.every(ds => ds.points.length < 4)"
          #crosshairs
        >
          Return none
        </template>
      </ScatterChart>
    </div>
    <CustomLegend
      v-if="drawLegend"
      v-model="activeDataSets"
      :model="allDataSets"
      :static="isStatic"
      :horizontal="isHorizontal"
    >
      <template #footer>
        <div
          v-if="props.pumpThresholds === undefined && !hideThresholdControl"
          class="flex w-12 justify-content-end"
        >
          <LabelledToggle
            v-model="showPumpThresholds"
            text="Show Pump Threshold"
          />
        </div>
      </template>
    </CustomLegend>
    <div class="flex justify-content-between"></div>
  </div>
</template>

<script setup lang="ts">
import LabelledToggle from "@/components/LabelledToggle.vue";

import {
  isNumberDomain,
  ScatterChart,
  LinePlot,
  NearestPoint,
  ThresholdArea,
  Axis,
  useAggregateDomains
} from "@incytestudios/ddv";
import type { DataSet } from "@incytestudios/ddv/types/common";
import { computed, inject, ref } from "vue";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { Correlations } from "@/composables/forms/formTypes";
import CustomLegend from "./CustomLegend.vue";
import { useGasCurveChartData } from "@/libs/charting/gasCurve/useGasCurveChartData";
import { injectStrict } from "@/utils/utils";
import { scenarioCalculationsKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";

const props = withDefaults(
  defineProps<{
    scenarioCalculations?: ScenarioCalculations;
    correlations?: Correlations;
    displayPumpThresholds?: boolean;
    drawLegend?: boolean;
    isHorizontal?: boolean;
    isStatic?: boolean;
    hideThresholdControl?: boolean;
  }>(),
  {
    pumpThresholds: undefined,
    scenarioCalculations: undefined,
    correlations: undefined,
    displayPumpThresholds: false
  }
);

const ddvTickFrequencies = inject<{ x: string; y: string }>(
  "ddvTickFrequencies"
);
const _scenarioCalculations =
  props.scenarioCalculations ?? injectStrict(scenarioCalculationsKey);
const showPumpThresholds = ref(!!props.displayPumpThresholds);
const chartData = computed(() => useGasCurveChartData(_scenarioCalculations));
const pumpThresholds = computed(() => chartData.value?.pumpThresholds);

const allDataSets = computed<DataSet[]>(() =>
  chartData.value
    ? [chartData.value.freeGasDataSet, chartData.value.maxGasAllowedDataSet]
    : []
);

const activeDataSets = ref(allDataSets.value.map(() => true));

const { aggregateX, aggregateY } = useAggregateDomains(
  allDataSets,
  ref([]),
  ref(false)
);
</script>

<style lang="scss" scoped>
.gas-curve-chart {
  display: grid;
  grid-template-columns: 3fr max-content;

  &.is-horizontal {
    grid-template-columns: 1fr;
  }

  &.is-static {
    :deep(.ddv) {
      &.isPlotHovered {
        cursor: auto !important;
      }
    }
  }
}

.threshold-toggle {
  vertical-align: top;
  line-height: 1.75rem;
}

@media screen and (max-width: 870px) {
  .gas-curve-chart {
    width: 95%;
    display: flex;
    flex-wrap: wrap;

    .scatter {
      width: 100%;
    }
  }
}
</style>
